<template>
    <div v-if="loaded">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo"
                            :max-date="additionals.dataAtual"
                            :range-inline="false"
                            :start-validations="validations.periodo"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableUnidade"
                            :options="form.unidades"
                            @change="loadFrom('unidade')"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableInfraestrutura"
                            :options="form.infraestruturas"
                            @change="loadFrom('infraestrutura')"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableOcupacao"
                            :options="form.ocupacoes"
                            @change="loadFrom('ocupacao')"
                            default-option-text="Todas"
                            label="CBO"
                            show-default-option
                            text-field="descricao"
                            v-model="model.ocupacaoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableUsuario"
                            :options="form.usuarios"
                            @change="loadFrom('usuario')"
                            default-option-text="Todos"
                            label="Profissional"
                            show-default-option
                            text-field="nome"
                            v-model="model.usuarioId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableProcedimento"
                            :options="form.procedimentos"
                            default-option-text="Todos"
                            label="Procedimentos"
                            show-default-option
                            text-field="descricao"
                            v-model="model.procedimentoCodigo"
                            value-field="codigo"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-input
                            :validations="validations.idadeDe"
                            label="Idade (anos) de"
                            number
                            v-model="model.idadeDe"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            :validations="validations.idadeAte"
                            label="Até"
                            number
                            v-model="model.idadeAte"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedCols"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="exportTo"
                    color="info"
                    small
                    title="Exportar"
                >
                    <rkm-icon icon="file-export" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="fields"
                :items="itens"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                @sort-changed="onSortChanged"
                class="table-resultado"
            >
                <template #cell(ocupacaoCodigo)="{ value }">
                    {{ value || '-' }}
                </template>

                <template
                    v-if="itens.length > 0"
                    #afterTable
                >
                    <div class="text-center table-footer">
                        Total de procedimentos: {{ getSafely(() => totalizadores.quantidade, 0) }}
                    </div>
                </template>
            </rkm-table>
        </rkm-area>
    </div>
</template>

<script>
import { mapActions } from 'vuex'
import { repository, formats, error, data, object, stringCase, file } from '@/mixins'

export default {
    computed: {
        validations() {

            const validations = {
                periodo: { required: true },
            }

            if (this.model.idadeAte) {

                validations.idadeDe = { max_value: this.model.idadeAte }
            }

            if (this.model.idadeDe) {

                validations.idadeAte = { min_value: this.model.idadeDe }
            }

            return validations
        },
        disableUnidade() {

            return !this.isPeriodoSelecionado
        },
        disableInfraestrutura() {

            return !this.isPeriodoSelecionado
                || this.model.unidadeId === null
        },
        disableOcupacao() {

            return !this.isPeriodoSelecionado
        },
        disableUsuario() {

            return !this.isPeriodoSelecionado
        },
        disableProcedimento() {

            return !this.isPeriodoSelecionado
        },
        isPeriodoSelecionado() {

            return this.model !== null
                && (this.model.periodo.start instanceof Date)
                && (this.model.periodo.end instanceof Date)
        },
        optionalColumns() {

            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {

            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
    },
    created() {

        this.repositories = {
            exportacao: this.getConectaSusRepository('exportacao.ambulatorio'),
            impressao: this.getConectaSusRepository('impressao.ambulatorio'),
            relatorioQuantitativoProducao: this.getConectaSusRepository('ambulatorio.relatorioQuantitativoProducao'),
        }

        this.load()
    },
    data() {

        return {
            loaded: false,
            additionals: null,
            form: null,
            model: null,
            initials: {
                form: null,
                model: null,
            },
            columns: [
                { key: 'procedimentoCodigo', label: 'Código do procedimento', sortable: true, fixed: true },
                { key: 'procedimentoNome', label: 'Nome do procedimento', sortable: true },
                { key: 'ocupacaoCodigo', label: 'Código do CBO', sortable: true, fixed: true },
                { key: 'ocupacaoNome', label: 'Nome do CBO', sortable: true },
                { key: 'usuarioNome', label: 'Nome profissional', sortable: true },
                { key: 'quantidade', label: 'Quantidade', class: 'col-min text-center', fixed: true },
            ],
            itens: [],
            totalizadores: null,
            selectedCols: [],
            selectedAll: false,
            indeterminate: false,
            sortBy: 'procedimentoCodigo',
            sortDesc: false,
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            this.repositories.relatorioQuantitativoProducao.list()
                .then(({ data }) => {

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.initials.form = this.clone(data.form)
                    this.initials.model = this.clone(data.model)

                    this.addWatchers()

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchers() {

            this.$watch('model.periodo', {
                deep: true,
                handler(periodo) {

                    if (periodo.start === null || periodo.end === null) {

                        this.resetAfterPeriodo()
                    }
                    else {

                        this.loadFrom('periodo')
                    }
                }
            })
        },
        loadFrom(field) {

            this.clearFrom(field)

            this.setLoading()

            this.repositories.relatorioQuantitativoProducao.list({ ...this.prepareData(), load_from: this.camelToSnake(field) })
                .then(({ data }) => {

                    if ('form' in data) {

                        Object.keys(data.form).forEach(key => this.$set(this.form, key, data.form[key]))
                    }

                    Object.keys(data.model).forEach(key => this.$set(this.model, key, data.model[key]))
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        clearFrom(field) {

            if (field == 'periodo') {

                return
            }

            field = field === 'procedimento' ? `${field}Codigo` : `${field}Id`

            const fields = [
                'unidadeId',
                'infraestruturaId',
                'ocupacaoId',
                'usuarioId',
                'procedimentoCodigo',
            ]

            fields.slice(fields.indexOf(field) + 1, fields.length)
                .forEach(key => this.$set(this.model, key, null))
        },
        search() {

            this.setLoading()

            this.repositories.relatorioQuantitativoProducao.index(this.prepareData())
                .then(({ data }) => {

                    this.itens = data.procedimentos
                    this.totalizadores = data.totalizadores
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {

            this.form = this.clone(this.initials.form)
            this.model = this.clone(this.initials.model)

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        resetAfterPeriodo() {

            this.form = this.clone(this.initials.form)

            const model = this.clone(this.initials.model)

            delete model.periodo

            Object.keys(model).forEach(key => this.$set(this.model, key, model[key]))
        },
        print() {

            this.setLoading()

            this.repositories.impressao.relatorioQuantitativoProducao(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        prepareDataWithColumns() {

            const data = this.prepareData()

            data.sortBy = this.sortBy ? this.camelToSnake(this.sortBy) : null
            data.sortDesc = this.sortDesc

            data.columns = this.fields.map(f => ({
                key: this.camelToSnake(f.key),
                label: f.label,
                class: f.class
            }))

            return data
        },
        prepareData() {

            const data = this.clone(this.model)

            this.formateDates(data.periodo, ['start', 'end'], 'DD/MM/YYYY')

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        selectAll(selected) {

            this.selectedCols = selected ? this.optionalColumns.map(c => c.key) : []
        },
        onSortChanged(context) {

            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
        exportTo() {

            this.setLoading()

            this.repositories.exportacao.relatorioQuantitativoProducao(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
    },
    mixins: [repository, formats, error, data, object, stringCase, file],
    watch: {
        model: {
            deep: true,
            handler() {

                this.itens = []
                this.totalizadores = null
            }
        },
        selectedCols(newValue) {

            this.selectedAll = newValue.length > 0 && newValue.length == this.optionalColumns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.optionalColumns.length
        },
    }
}
</script>

<style scoped>
.dropdown >>> .auto-scroll {
    max-height: 400px;
    overflow-y: auto;
    width: 400px;
}

.table-resultado >>> .table-footer {
    color: var(--gray-dark);
    font-weight: 600;
    background: #f1f1f1;
    padding: 0.5rem;
    border: 1px solid #dee2e6;
}
</style>